import { auth, db } from '@/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { getDoc, doc, setDoc } from 'firebase/firestore';

export const register = async (email, password, nickname, nombre) => {
    try {
      // Crear usuario con las credenciales de email y contraseña
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;  // Aquí es donde obtienes el usuario
  
      // Verifica que el usuario esté autenticado
      if (user) {
        // Ahora puedes actualizar el displayName
        await updateProfile(user, { displayName: nickname }); // Asegúrate de importar updateProfile correctamente
      }
  
      // Datos predeterminados para el nuevo usuario
      const defaultPhoto = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSlbUvJQpkNm-m4qOhZPVS5oqHD6gZIdfv2BA&s"; // Foto predeterminada
      const defaultDescription = '¡Bienvenido a mi perfil! Trátame bien, soy nuevo.'; // Descripción predeterminada
  
      // Guardar los datos del usuario en Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: email,
        nickname: nickname,
        nombre: nombre,
        descripcion: defaultDescription, // Descripción predeterminada
        foto: defaultPhoto, // Foto predeterminada
      });

      // También puedes actualizar el perfil del usuario en Firebase Authentication
      await updateProfile(user, {
          displayName: nickname,  // Usando el nickname
          photoURL: defaultPhoto,  // Usando la foto predeterminada
      });
  
      return user;
    } catch (error) {
      // Capturar y lanzar error
      if (error.code === "auth/email-already-in-use") {
        // El correo ya está en uso
        throw new Error("El correo electrónico ya está registrado.");
      } else {
        // Otros errores de Firebase
        console.error("Error en el registro: ", error.message);
        throw new Error("Error al registrar usuario: " + error.message);
      }
    }
  };
  
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    let errorMessage = '';

    // Manejo de errores específicos
    if (error.code === 'auth/invalid-email') {
      errorMessage = 'El correo electrónico no tiene un formato válido.';
    } else if (error.code === 'auth/user-not-found') {
      errorMessage = 'No se encontró una cuenta con ese correo electrónico.';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'La contraseña es incorrecta.';
    } else {
      errorMessage = 'Error al iniciar sesión. Intenta de nuevo más tarde.';
    }

    console.error("Error en el inicio de sesión: ", errorMessage);
    throw new Error(errorMessage);
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error al cerrar sesión: ", error.message);
    throw error;
  }
};

export const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Error al enviar correo de restablecimiento: ", error.message);
      throw error;
    }
  };

export const onAuthStateChangedListener = (callback) => {
  return onAuthStateChanged(auth, callback);
};

export const createClass = async (claseData) => {
  try {
    const docRef = await setDoc(doc(db, 'clases', claseData.id), claseData);
    return docRef;
  } catch (error) {
    console.error("Error al crear clase: ", error.message);
    throw error;
  }
};

/**
 * Obtiene los datos básicos del usuario desde Firebase Authentication o Firestore.
 * @param {string} nickname - Nickname del usuario.
 * @returns {Promise<Object>} - Datos del usuario.
 */
export const fetchUserData = async (nickname) => {
    try {
      if (!nickname || typeof nickname !== "string") {
        throw new Error("El nickname del usuario es inválido o está vacío.");
      }
  
      // Obtén el usuario autenticado desde Firebase Authentication
      const user = auth.currentUser;
  
      if (user) {
        // Si el usuario está autenticado, se verifica si el displayName coincide con el nickname
        if (user.displayName === nickname) {
          // Si coincide, obtenemos los datos desde Firebase Authentication
          return {
            displayName: user.displayName,
            email: user.email,
            photoURL: user.photoURL,
          };
        } else {
          // Si el displayName no coincide, lanzamos un error
          throw new Error("El nickname no coincide con el usuario autenticado.");
        }
      } else {
        // Si no hay usuario autenticado, intentamos buscarlo en Firestore
        const userDocRef = doc(db, "users", nickname);  // Usamos el nickname como ID en Firestore
        const userDoc = await getDoc(userDocRef);
  
        if (!userDoc.exists()) {
          throw new Error("El usuario no existe en Firestore.");
        }
  
        // Si se encuentra el documento en Firestore, devolvemos los datos
        const userData = userDoc.data();
        return {
          displayName: userData.nickname,
          email: userData.email,
          photoURL: userData.foto,
        };
      }
    } catch (error) {
      console.error("Error en fetchUserData:", error);
      throw error;
    }
  }; 